import React, { useState, useEffect } from 'react';
import { Form, Input, Button, DatePicker, Row, Col, message, Select, ConfigProvider } from 'antd';
import { createLicense, updateLicense } from '../services/api';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import 'dayjs/locale/zh-cn';
import zhCN from 'antd/locale/zh_CN';
import './LicenseForm.css';

// 扩展dayjs插件和设置中文语言
dayjs.extend(customParseFormat);
dayjs.locale('zh-cn');

// 日期格式
const dateFormat = 'YYYY-MM-DD';

const { TextArea } = Input;
const { Option } = Select;

// 版本配置
const VERSION_OPTIONS = [
  { value: 1, label: '版本1 (旧版)' },
  { value: 2, label: '版本2 (新版)' },
  // 在这里添加更多版本
];

// 获取默认日期
const getCurrentDate = () => dayjs();
const getExpiryDate = (startDate) => dayjs(startDate).add(2, 'years').subtract(1, 'days');

// 格式化有效期 - 根据版本使用不同格式
const formatValidityPeriod = (issueDate, version) => {
  const startDate = dayjs(issueDate);
  const endDate = getExpiryDate(startDate);

  if (version === 1) {
    // v1版本格式：2025年08月02日至2027年08月01日
    return `${startDate.format('YYYY年MM月DD日')}至${endDate.format('YYYY年MM月DD日')}`;
  } else {
    // v2版本格式：2027-08-01
    return endDate.format('YYYY-MM-DD');
  }
};

// 默认值
const DEFAULT_VALUES = {
  type: '有限责任公司(自然人独资)',
  category: '内资演出经纪机构',
  registeredCapital: '10万元',
  businessScope: '经营演出及经纪业务',
  issuingAuthority: '上海市文化体育和旅游局',
  version: 2,
  initialIssueDate: getCurrentDate(),
  issueDate: getCurrentDate()
};

function LicenseForm({ initialValues, onSuccess, onCancel }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [currentVersion, setCurrentVersion] = useState(DEFAULT_VALUES.version);
  const isEditing = !!initialValues;

  // 合并默认值和初始值
  const formattedInitialValues = initialValues 
    ? {
        ...DEFAULT_VALUES,
        ...initialValues,
        initialIssueDate: initialValues.initialIssueDate ? dayjs(initialValues.initialIssueDate, dateFormat) : getCurrentDate(),
        issueDate: initialValues.issueDate ? dayjs(initialValues.issueDate, dateFormat) : getCurrentDate(),
        version: initialValues.version || DEFAULT_VALUES.version,
      }
    : DEFAULT_VALUES;

  // 初始化时设置有效期
  useEffect(() => {
    const version = form.getFieldValue('version');
    const issueDate = form.getFieldValue('issueDate');
    if (issueDate) {
      const validityPeriod = formatValidityPeriod(issueDate, version);
      form.setFieldsValue({ validityPeriod });
    }
  }, [form]);

  // 处理版本变化
  const handleVersionChange = (version) => {
    setCurrentVersion(version);
    const issueDate = form.getFieldValue('issueDate');
    if (issueDate) {
      const validityPeriod = formatValidityPeriod(issueDate, version);
      form.setFieldsValue({ validityPeriod });
    }
  };

  // 处理发照日期变化
  const handleIssueDateChange = (date) => {
    if (date) {
      const validityPeriod = formatValidityPeriod(date, currentVersion);
      form.setFieldsValue({ validityPeriod });
    }
  };

  const onFinish = async (values) => {
    try {
      setLoading(true);
      const formData = {
        ...values,
        initialIssueDate: values.initialIssueDate?.format(dateFormat),
        issueDate: values.issueDate?.format(dateFormat),
        version: parseInt(values.version, 10),
      };

      if (isEditing) {
        await updateLicense(initialValues.id, formData);
        message.success('更新成功');
      } else {
        await createLicense(formData);
        message.success('创建成功');
      }

      onSuccess();
    } catch (error) {
      message.error(isEditing ? '更新失败' : '创建失败');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ConfigProvider locale={zhCN}>
      <Form
        form={form}
        layout="vertical"
        initialValues={formattedInitialValues}
        onFinish={onFinish}
        className="license-form"
      >
      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Form.Item 
            name="version" 
            label="营业执照版本" 
            rules={[{ required: true, message: '请选择营业执照版本' }]}
            tooltip="选择不同版本会影响营业执照的展示样式和有效期格式"
          >
            <Select 
              placeholder="请选择营业执照版本"
              onChange={handleVersionChange}
            >
              {VERSION_OPTIONS.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item 
            name="number" 
            label="执照编号" 
            rules={[{ required: true, message: '请输入执照编号' }]}
          >
            <Input placeholder="请输入执照编号" />
          </Form.Item>
        </Col>
      </Row>
      
      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Form.Item 
            name="name" 
            label="公司名称" 
            rules={[{ required: true, message: '请输入公司名称' }]}
          >
            <Input placeholder="请输入公司名称" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item 
            name="legalRepresentative" 
            label="法定代表人" 
            rules={[{ required: true, message: '请输入法定代表人' }]}
          >
            <Input placeholder="请输入法定代表人" />
          </Form.Item>
        </Col>
      </Row>
      
      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Form.Item 
            name="type" 
            label="公司类型" 
            rules={[{ required: true, message: '请输入公司类型' }]}
          >
            <Input placeholder="请输入公司类型" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item 
            name="category" 
            label="单位类别" 
            rules={[{ required: true, message: '请输入单位类别' }]}
          >
            <Input placeholder="请输入单位类别" />
          </Form.Item>
        </Col>
      </Row>
      
      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Form.Item 
            name="registeredCapital" 
            label="注册资本" 
            rules={[{ required: true, message: '请输入注册资本' }]}
          >
            <Input placeholder="请输入注册资本" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item 
            name="initialIssueDate" 
            label="首次发照日期" 
            rules={[{ required: true, message: '请选择首次发照日期' }]}
          >
            <DatePicker 
              style={{ width: '100%' }} 
              format={dateFormat}
              placeholder="请选择日期"
              allowClear={false}
              showToday
              getPopupContainer={trigger => trigger.parentNode}
            />
          </Form.Item>
        </Col>
      </Row>
      
      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Form.Item 
            name="issueDate" 
            label="发照日期" 
            rules={[{ required: true, message: '请选择发照日期' }]}
          >
            <DatePicker 
              style={{ width: '100%' }} 
              format={dateFormat}
              onChange={handleIssueDateChange}
              placeholder="请选择日期"
              allowClear={false}
              showToday
              getPopupContainer={trigger => trigger.parentNode}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item 
            name="validityPeriod" 
            label="有效期" 
            rules={[{ required: true, message: '请输入有效期' }]}
            tooltip={currentVersion === 1 ? 
              "格式：2025年08月02日至2027年08月01日" : 
              "格式：2027-08-01"}
          >
            <Input 
              placeholder={currentVersion === 1 ? 
                "示例：2025年08月02日至2027年08月01日" : 
                "示例：2027-08-01"} 
            />
          </Form.Item>
        </Col>
      </Row>
      
      <Row gutter={16}>
        <Col xs={24}>
          <Form.Item 
            name="issuingAuthority" 
            label="发照机关" 
            rules={[{ required: true, message: '请输入发照机关' }]}
          >
            <Input placeholder="请输入发照机关" />
          </Form.Item>
        </Col>
      </Row>
      
      <Form.Item 
        name="address" 
        label="公司地址" 
        rules={[{ required: true, message: '请输入公司地址' }]}
      >
        <Input placeholder="请输入公司地址" />
      </Form.Item>
      
      <Form.Item 
        name="businessScope" 
        label="经营范围" 
        rules={[{ required: true, message: '请输入经营范围' }]}
      >
        <TextArea rows={4} placeholder="请输入经营范围" />
      </Form.Item>
      
      <div className="form-actions">
        <Button type="primary" htmlType="submit" loading={loading}>
          {isEditing ? '更新' : '创建'}
        </Button>
        <Button onClick={onCancel}>取消</Button>
      </div>
      </Form>
    </ConfigProvider>
  );
}

export default LicenseForm; 